import { ReactElement } from 'react';

import {
    Box,
    Button,
    InputBase,
    MenuItem,
    Select,
    SelectChangeEvent,
    SxProps,
    Typography,
} from '@mui/material';

type SingleValueFilterMenuItemProps = {
    title: string;
    options: string[] | [string, string][];
    value: string | [string, string] | null;
    onChange: (value: string | [string, string]) => void;
    onClearFilter: () => void;
};

const containerStyles: SxProps = {
    borderBottom: '1px solid #E5E5E5',
    display: 'flex',
    flexDirection: 'column',
    width: 400,
    padding: '10px 20px',
};

const selectStyles: SxProps = {
    background: '#fff',
    borderRadius: '5px',
    border: '1px solid #E5E5E5',
    minWidth: 150,
    height: 33,
    fontSize: 10,
    fontWeight: 400,
    pl: '8px',
    display: 'flex',
    alignItems: 'center',
    marginLeft: '15px',
    marginRight: 'auto',
    '& .MuiSelect-select': {
        display: 'flex',
        alignItems: 'flex-start',
        padding: 0,
    },
};

const getDotStyles = (color: string = '#F59E42'): SxProps => {
    return {
        display: 'inline-block',
        width: '14px',
        height: '14px',
        borderRadius: '50%',
        background: color,
        marginRight: 2,
    };
};

export default function SingleValueFilterMenuItem({
    title,
    options,
    value,
    onChange,
    onClearFilter,
}: SingleValueFilterMenuItemProps) {
    const valueDisplay = (colour: string, displayValue: string): ReactElement => {
        return (
            <Box display="flex" alignItems="center">
                {colour && <Box sx={getDotStyles(colour)} />}
                <Typography fontSize={14} fontWeight={400}>
                    {displayValue}
                </Typography>
            </Box>
        );
    };

    const selectedOption = (option: string | [string, string]) => {
        if (Array.isArray(option)) {
            return option[1];
        }
        return option;
    };

    return (
        <Box sx={containerStyles}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography sx={{ color: '#000000' }} fontWeight={400} fontSize={14}>
                    {title}
                </Typography>
                <Select
                    value={selectedOption(value || '')}
                    onChange={(e: SelectChangeEvent) => {
                        const selectedValue = e.target.value as string;
                        const selectedOption = options.find((option: string | [string, string]) => {
                            if (Array.isArray(option)) {
                                return option[1] === selectedValue;
                            }
                            return option === selectedValue;
                        });
                        return onChange(selectedOption);
                    }}
                    input={<InputBase />}
                    sx={selectStyles}
                    renderValue={() => {
                        const colour = Array.isArray(value) ? value[0] : undefined;
                        const displayValue = Array.isArray(value) ? value[1] : value;
                        return valueDisplay(colour, displayValue);
                    }}
                >
                    {options.map((option: string | [string, string]) => {
                        const colour = Array.isArray(option) ? option[0] : undefined;
                        const displayValue = Array.isArray(option) ? option[1] : option;
                        return (
                            <MenuItem key={displayValue} value={displayValue}>
                                {valueDisplay(colour, displayValue)}
                            </MenuItem>
                        );
                    })}
                </Select>
                <Button
                    onClick={onClearFilter}
                    sx={{
                        textTransform: 'none',
                        color: '#444444',
                        fontSize: '0.9rem',
                    }}
                >
                    Clear
                </Button>
            </Box>
        </Box>
    );
}
